import { useState, useEffect } from "react"
import { toast } from "sonner"
import { useNavigate } from "react-router-dom"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import { apiClient as api } from "@/lib/api-client"
import { useDispatch } from "react-redux"
import { AppDispatch } from "@/store"
import { fetchUserProfile } from "@/modules/auth/store/authSlice"
import { useAuth } from "@/modules/auth/hooks/use-auth"
import { AppRole } from "@/modules/auth/constants/app-role"

export default function AcceptLegalDocumentsPage() {
  const [legalDocs, setLegalDocs] = useState<{
    terms?: { version: string }
    privacy?: { version: string }
  } | null>(null)
  const [agreedTerms, setAgreedTerms] = useState(false)
  const [agreedPrivacy, setAgreedPrivacy] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { hasRole } = useAuth()

  useEffect(() => {
    async function fetchDocs() {
      try {
        const response = await api.get("/legal-documents")
        setLegalDocs(response.data)
      } catch (_err) {
        toast.error("Failed to load legal documents. Please try again.")
      }
    }
    fetchDocs()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!legalDocs?.terms?.version || !legalDocs?.privacy?.version) {
      toast.error("Legal document versions are not loaded. Please refresh.")
      return
    }

    setIsSubmitting(true)
    try {
      await api.post("/legal-documents/accept-consent", {
        acceptedTermsVersion: legalDocs.terms.version,
        acceptedPrivacyVersion: legalDocs.privacy.version,
      })

      await dispatch(fetchUserProfile()).unwrap()

      toast.success("Legal documents accepted successfully.")
      navigate(hasRole(AppRole.Employee) ? "/employee/dashboard" : "/dashboard")
    } catch (_error) {
      toast.error("Failed to accept legal documents. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col md:flex-row w-full min-h-screen bg-muted items-center justify-center p-4">
      <Card className="shadow-lg border border-border w-full max-w-lg">
        <CardHeader>
          <CardTitle className="text-2xl text-bold text-[#1A237E]">
            Action Required
          </CardTitle>
          <CardDescription className="text-base text-muted-foreground">
            We have updated our Terms & Conditions and Privacy Policy. Please
            review and accept them to continue using your account.
          </CardDescription>
        </CardHeader>

        <CardContent className="pb-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-col gap-4 mt-2 mb-6">
              <div className="flex items-start space-x-3 bg-blue-50 p-4 rounded-md">
                <input
                  type="checkbox"
                  id="agreedTerms"
                  checked={agreedTerms}
                  onChange={(e) => setAgreedTerms(e.target.checked)}
                  className="w-5 h-5 mt-0.5 cursor-pointer accent-[#1A237E]"
                />
                <label
                  htmlFor="agreedTerms"
                  className="text-sm font-medium leading-snug cursor-pointer text-foreground"
                >
                  I have read and agree to the latest{" "}
                  <a
                    href="/legal/terms"
                    target="_blank"
                    className="text-[#40C4FF] hover:underline font-bold whitespace-nowrap"
                  >
                    Terms & Conditions
                  </a>
                </label>
              </div>
              <div className="flex items-start space-x-3 bg-blue-50 p-4 rounded-md">
                <input
                  type="checkbox"
                  id="agreedPrivacy"
                  checked={agreedPrivacy}
                  onChange={(e) => setAgreedPrivacy(e.target.checked)}
                  className="w-5 h-5 mt-0.5 cursor-pointer accent-[#1A237E]"
                />
                <label
                  htmlFor="agreedPrivacy"
                  className="text-sm font-medium leading-snug cursor-pointer text-foreground"
                >
                  I have read and agree to the latest{" "}
                  <a
                    href="/legal/privacy"
                    target="_blank"
                    className="text-[#40C4FF] hover:underline font-bold whitespace-nowrap"
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>
            </div>

            <Button
              id="accept-docs-button"
              type="submit"
              className="w-full text-white py-6 text-base font-semibold transition-colors"
              style={{ backgroundColor: "#1A237E" }}
              disabled={
                isSubmitting ||
                !agreedTerms ||
                !agreedPrivacy ||
                !legalDocs?.terms ||
                !legalDocs?.privacy
              }
            >
              {isSubmitting ? "Accepting..." : "Accept & Continue"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
